import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { BLOCK_INFO_API, BLOCK_PARAM, MERGE_BLOCK, isNumber } from "../utils/constants";
import BlockInfoUI from '../ui/block_info.js';
import BlockCompositionUI from '../ui/block_composition.js';
import SlotProgressUI from '../ui/slot_progress.js';
import BlockValueUI from '../ui/block_value.js';
import BlockBidUI from '../ui/block_bid.js';
import TxTableUI from '../ui/tx_table.js';

import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import { IconButton } from '@mui/material';

function BlockInfoView() {
    const { blockNumber } = useParams();
    const navigate = useNavigate();
    const [searchValue, setSearchValue] = useState(blockNumber || "");
    const [filterValue, setFilterValue] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(true);
    const [blockData, setBlockData] = useState(null);

    const uiBlockInfoRef = useRef(null);
    const uiBlockCompositionRef = useRef(null);
    const uiSlotProgressRef = useRef(null);
    const uiBlockValueRef = useRef(null);
    const uiBlockBidRef = useRef(null);
    const uiTxTableRef = useRef(null);

    useEffect(() => {
        if (blockNumber) {
            setSearchValue(blockNumber);
            loadBlock(blockNumber);
        }
    }, [blockNumber]);

    useEffect(() => {
        if (!loading && blockData) {
            uiBlockInfoRef.current = new BlockInfoUI("#block_info");
            uiBlockCompositionRef.current = new BlockCompositionUI("#block_composition_value", "#block_composition_gas");
            uiSlotProgressRef.current = new SlotProgressUI("#slot_progress_vh");
            uiBlockValueRef.current = new BlockValueUI("#block_value_vh");
            uiBlockBidRef.current = new BlockBidUI("#block_bid_vh");
            uiTxTableRef.current = new TxTableUI("#tx_table", blockData, uiBlockCompositionRef.current);

            uiBlockValueRef.current.setBlockBidUI(uiBlockBidRef.current);
            uiSlotProgressRef.current.setBlockBidUI(uiBlockBidRef.current);
            uiBlockValueRef.current.setProgressUI(uiSlotProgressRef.current);

            uiBlockInfoRef.current.setBlockInfo(blockData);
            uiBlockCompositionRef.current.setBlockInfo(blockData);
            uiBlockValueRef.current.setBlockInfo(blockData);
            uiBlockBidRef.current.setBlockInfo(blockData);
            uiSlotProgressRef.current.setBlockInfo(blockData);
        }
    }, [loading, blockData]);

    const loadBlock = (block) => {
        if (!isNumber(block) || block < MERGE_BLOCK) {
            let str = "";
            if (!isNumber(block)) {
                str = `'${block}' is not a valid block number.`;
            } else if (block < MERGE_BLOCK) {
                str = `Blocks before merge (<${MERGE_BLOCK}) not supported.`;
            }
            setError(str);
            setLoading(false);
            return;
        }

        setError("");
        setLoading(true);
        window.document.title = "Block " + block;

        fetch(BLOCK_INFO_API + "?" + BLOCK_PARAM + "=" + block)
            .then(response => response.json())
            .then(data => {
                setBlockData(data);
                setLoading(false);
            })
            .catch(error => {
                console.error(error);
                setError(`Unable to retrieve information for block ${block}.`);
                setLoading(false);
            });
    };

    const handleSearch = () => {
        if (searchValue) {
            navigate(`/data/${searchValue}`);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter") {
            handleSearch();
        }
    };

    const goToPrevious = () => {
        const prevBlock = parseInt(blockNumber) - 1;
        navigate(`/data/${prevBlock}`);
    };

    const goToNext = () => {
        const nextBlock = parseInt(blockNumber) + 1;
        navigate(`/data/${nextBlock}`);
    };

    const handleResetFilter = () => {
        setFilterValue("");
        const filterInput = document.getElementById('filter-input-tx-table');
        if (filterInput) {
            filterInput.value = '';
            filterInput.dispatchEvent(new Event('input', { bubbles: true }));
        }
    };


    return (
        <>
            {!error && !loading && (
                <div className="responsive-grid">
                    <div className="left-section">
                        <div className="headline">
                            <div className="headline-left">
                                <span>Block <span id="block_info_number"></span></span>
                                <button id="previous-button" onClick={goToPrevious}>&lt;</button>
                                <button id="next-button" onClick={goToNext}>&gt;</button>
                            </div>
                        </div>
                        {error && <div className="error-text" style={{ marginTop: '10px' }}>{error}</div>}
                        <div id="block_info"></div>
                        <div id="copy-overlay"></div>

                        <div className="headlines">
                            <div className="half-headline">
                                <div className="tooltip">
                                    <div className="section-heading graph-heading">Value Over Time</div>
                                    <span className="tooltiptext" style={{ width: '600px' }}>
                                        Temporal course of the highest bid submitted across all builders.
                                        <br /><br />
                                        <strong>Lines:</strong>
                                        <ul>
                                            <li><strong>Horizontal <span style={{ color: '#26a6ed' }}>Blue:</span></strong> Winning builder submission that was written to the blockchain.</li>
                                            <li><strong>Horizontal <span style={{ color: '#f9f871' }}>Yellow:</span></strong> Best submission for the slot.</li>
                                            <li><strong>Horizontal <span style={{ color: '#fb717f' }}>Red:</span></strong> Vanilla/Private block where no matching bid got found.</li>
                                        </ul>
                                    </span>
                                </div>
                            </div>
                            <div className="half-headline">
                                <div className="tooltip">
                                    <div className="section-heading graph-heading">Builder Bids</div>
                                    <span className="tooltiptext" style={{ width: '600px' }}>
                                        Highest bid submitted by each builder. Bids are sorted from highest to lowest value. Gas usage is shown in the respective small green bar. The total bid count per builder is depicted on the left.
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="graphs">
                            <div className="half-graph" id="block_value_vh"></div>
                            <div className="half-graph" id="block_bid_vh"></div>
                        </div>
                        <div className="progress-bar-container">
                            <div className="progress-bar" id="slot_progress_vh"></div>
                        </div>
                    </div>

                    <div className="right-section">
                        <div className="section-heading graph-heading">Transactions</div>

                        <div className="controls-bar">
                            <div className="search-group">
                                <input
                                    type="text"
                                    id="search-input"
                                    value={searchValue}
                                    onChange={(e) => setSearchValue(e.target.value)}
                                    onKeyDown={handleKeyDown}
                                    placeholder="Search #Block..."
                                />
                                <IconButton
                                    onClick={handleSearch}
                                    sx={{
                                        color: '#d3d3d3',
                                        '&:hover': { backgroundColor: 'rgba(211, 211, 211, 0.1)' }
                                    }}
                                >
                                    <SearchIcon />
                                </IconButton>
                            </div>

                            <div className="filter-group">
                                <input
                                    type="text"
                                    id="filter-input-tx-table"
                                    value={filterValue}
                                    onChange={(e) => setFilterValue(e.target.value)}
                                    placeholder="Filter..."
                                />
                                <IconButton
                                    id="reset-button-tx"
                                    onClick={handleResetFilter}
                                    sx={{
                                        color: '#d3d3d3',
                                        '&:hover': { backgroundColor: 'rgba(211, 211, 211, 0.1)' }
                                    }}
                                >
                                    <RefreshIcon />
                                </IconButton>
                            </div>


                            <div className="filter-container">
                                <div className="filter-grid">
                                    <label>
                                        <input id="class-eth" type="checkbox" defaultChecked />
                                        <span style={{ color: '#26a6ed' }}>ETH</span>
                                    </label>
                                    <label>
                                        <input id="class-token" type="checkbox" defaultChecked />
                                        <span style={{ color: '#00A36C' }}>TOKEN</span>
                                    </label>
                                    <label>
                                        <input id="class-cex" type="checkbox" defaultChecked />
                                        <span style={{ color: '#ff697c' }}>CEX</span>
                                    </label>
                                    <label>
                                        <input id="class-dex" type="checkbox" defaultChecked />
                                        <span style={{ color: '#4b2caf' }}>DEX</span>
                                    </label>
                                    <label>
                                        <input id="class-defi" type="checkbox" defaultChecked />
                                        <span style={{ color: '#e59c23' }}>DEFI</span>
                                    </label>
                                    <label>
                                        <input id="class-mev" type="checkbox" defaultChecked />
                                        <span style={{ color: '#ffff00' }}>MEV</span>
                                    </label>
                                    <label>
                                        <input id="class-bridge" type="checkbox" defaultChecked />
                                        <span style={{ color: '#8c1434' }}>BRIDGE</span>
                                    </label>
                                    <label>
                                        <input id="class-other" type="checkbox" defaultChecked />
                                        <span style={{ color: '#d3d3d3' }}>OTHER</span>
                                    </label>
                                </div>
                            </div>
                        </div>

                        <div className="block-composition-graphs">
                            <div id="block_composition_value" style={{ height: '80px', width: '100%' }}></div>
                            <div id="block_composition_gas" style={{ height: '80px', width: '100%' }}></div>
                        </div>

                        <div className="table-container-flex" style={{ height: '600px', overflowY: 'auto' }}>
                            <div id="tx_table"></div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

export default BlockInfoView;